import { Bug, Info, Languages, LoaderCircle, Shield } from "lucide-react";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarMenu, SidebarMenuItem } from "@/components/ui/sidebar";
import { useState } from "react";
import { useLogoutMutation } from "@/api/mutations/AuthMutations";
import { useAuthenticatedUserQuery } from "@/api/queries/AuthQueries";
import { useCurrentUserQuery } from "@/api/queries/UserQueries";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { TwoFactorQuickSetup } from "@/components/forms/auth/TwoFactorQuickSetup";
import { Button } from "./ui/button";
import { UseTheme } from "./ThemeProvider";
import { ExitIcon, MoonIcon, SunIcon, DashboardIcon, ChatBubbleIcon } from "@radix-ui/react-icons";
import i18n from "@/utils/i18n/i18n";
import PushNotificationManager from "./push-notification-manager";
import { useNavigate } from "react-router-dom";
import useIsAdminPanel from "@/hooks/useIsAdminPanel";
import { useTranslation } from "react-i18next";
import AboutDialog from "./dialogs/AboutDialog";
import ReportBugDialog from "./dialogs/ReportBugDialog";
import { useBugReportShortcut } from "@/hooks/useKeyboardShortcut";

const countryOptions = [
    {
        value: "united-kingdom",
        name: "United Kingdom",
        code: "en",
        flagUrl: "/assets/flags/gb.webp",
        altText: "United Kingdom Flag",
    },
    {
        value: "france",
        name: "France",
        code: "fr",
        flagUrl: "/assets/flags/fr.webp",
        altText: "France Flag",
    },
    {
        value: "germany",
        name: "Germany",
        code: "de",
        flagUrl: "/assets/flags/de.webp",
        altText: "Germany Flag",
    },
    {
        value: "taiwan",
        name: "Taiwan (繁體中文)",
        code: "zh-TW",
        flagUrl: "/assets/flags/tw.webp",
        altText: "Taiwan Flag",
    },
];

export function NavUser() {
    const { t } = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const [show2FADialog, setShow2FADialog] = useState(false);
    const [showAboutDialog, setShowAboutDialog] = useState(false);
    const [showBugReportDialog, setShowBugReportDialog] = useState(false);
    const { theme, setTheme } = UseTheme();
    const { data } = useAuthenticatedUserQuery();
    const { data: currentUser } = useCurrentUserQuery();
    const { mutate } = useLogoutMutation(setIsLoading);
    const isAdminPanel = useIsAdminPanel();
    const navigate = useNavigate();

    // Determine the actual applied theme (handles "system" theme)
    const getAppliedTheme = () => {
        if (theme === "system") {
            return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
        }
        return theme;
    };

    const appliedTheme = getAppliedTheme();

    // Keyboard shortcut for bug report (F9)
    useBugReportShortcut(() => {
        setShowBugReportDialog(true);
    });

    const handleLogout = (e: any) => {
        e.preventDefault();
        mutate();
    };
    const handleLanguageChange = (language) => {
        console.log("language: ", language);
        i18n.changeLanguage(language);
        localStorage.setItem("locale", language);
        window.location.reload();
    };
    return (
        <SidebarMenu>
            <SidebarMenuItem>
                <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                        <Button
                            variant={"ghost"}
                            size="lg"
                            className="flex h-9 w-9 items-center rounded-full p-0 hover:bg-transparent active:bg-transparent"
                        >
                            <Avatar className="h-9 w-9">
                                <AvatarImage src={data?.user.image || ""} alt={`Image of ${data?.user.name}`} />
                                <AvatarFallback className="rounded-lg">
                                    {data?.user.name?.[0] || "U"}
                                    {data?.user.name?.split(" ")?.[1]?.[0] || ""}
                                </AvatarFallback>
                            </Avatar>
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                        className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                        side="bottom"
                        align="end"
                        sideOffset={4}
                    >
                        <DropdownMenuLabel className="p-0 font-normal">
                            <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                                <Avatar className="h-8 w-8 rounded-lg">
                                    <AvatarImage src={data?.user.image || ""} alt={`Image of ${data?.user.name}`} />
                                    <AvatarFallback className="rounded-lg">
                                        {data?.user.name?.[0] || "U"}
                                        {data?.user.name?.split(" ")?.[1]?.[0] || ""}
                                    </AvatarFallback>
                                </Avatar>
                                <div className="grid flex-1 text-left text-sm leading-tight">
                                    <span className="truncate font-semibold">{data?.user.name}</span>
                                    <span className="truncate text-xs">{data?.user.email}</span>
                                </div>
                            </div>
                        </DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        {
                            currentUser?.role === "admin" && (
                                <>
                                <DropdownMenuItem onClick={() => navigate(isAdminPanel ? "/" : "/admin")}>
                                    <div className="flex w-full justify-between">
                                        <span>{isAdminPanel ? t("userMenu.chatApplication") : t("userMenu.adminPanel")}</span>
                                        {isAdminPanel ? (
                                            <ChatBubbleIcon className="h-[1.2rem] w-[1.2rem]" />
                                        ) : (
                                            <DashboardIcon className="h-[1.2rem] w-[1.2rem]" />
                                        )}
                                    </div>
                                </DropdownMenuItem>
                        <DropdownMenuSeparator />
                                </>
                            )
                        }
                        <DropdownMenuGroup>
                            <DropdownMenuItem onClick={() => setTheme(appliedTheme === "dark" ? "light" : "dark")}>
                                <div className="flex w-full justify-between">
                                    <span>{appliedTheme === "light" ? t("userMenu.darkMode") : t("userMenu.lightMode")}</span>
                                    {appliedTheme === "light" ? (
                                        <MoonIcon className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100" />
                                    ) : (
                                        <SunIcon className="h-[1.2rem] w-[1.2rem] rotate-0 scale-100" />
                                    )}
                                </div>
                            </DropdownMenuItem>

                            <DropdownMenuSub>
                                <DropdownMenuSubTrigger
                                    ChevronIcon={() => {
                                        return <Languages className="ml-auto h-[1.2rem] w-[1.2rem]" />;
                                    }}
                                >
                                    {t("userMenu.language")}
                                </DropdownMenuSubTrigger>
                                <DropdownMenuPortal>
                                    <DropdownMenuSubContent>
                                        {countryOptions.map((country) => {
                                            return (
                                                <DropdownMenuItem
                                                    key={country.code}
                                                    onClick={() => handleLanguageChange(country.code)}
                                                >
                                                    <img
                                                        className="aspect-[3/2] h-6 w-auto rounded-sm border-[1px]"
                                                        src={country.flagUrl}
                                                        alt={country.altText}
                                                    />
                                                </DropdownMenuItem>
                                            );
                                        })}
                                    </DropdownMenuSubContent>
                                </DropdownMenuPortal>
                            </DropdownMenuSub>
                            <PushNotificationManager />
                            <Dialog
                                open={show2FADialog}
                                onOpenChange={(open) => {
                                    setShow2FADialog(open);
                                }}
                            >
                                <DialogTrigger asChild>
                                    <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
                                        <div className="flex w-full justify-between">
                                            <span>{t("userMenu.twoFactorAuth")}</span>
                                            <div className="flex items-center gap-1">
                                                {data?.user?.twoFactorEnabled && (
                                                    <div className="h-2 w-2 rounded-full bg-green-500" />
                                                )}
                                                <Shield className="h-[1.2rem] w-[1.2rem]" />
                                            </div>
                                        </div>
                                    </DropdownMenuItem>
                                </DialogTrigger>
                                <DialogContent className="max-w-lg">
                                    <TwoFactorQuickSetup
                                        onClose={() => {
                                            setShow2FADialog(false);
                                        }}
                                    />
                                </DialogContent>
                            </Dialog>
                        </DropdownMenuGroup>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                            onSelect={(e) => {
                                e.preventDefault();
                                setShowAboutDialog(true);
                            }}
                        >
                            <div className="flex w-full justify-between">
                                <span>{t("userMenu.about")}</span>
                                <Info className="h-[1.2rem] w-[1.2rem]" />
                            </div>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                            onSelect={(e) => {
                                e.preventDefault();
                                setShowBugReportDialog(true);
                            }}
                        >
                            <div className="flex w-full justify-between">
                                <span>{t("userMenu.reportBug")}</span>
                                <Bug className="h-[1.2rem] w-[1.2rem]" />
                            </div>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleLogout} disabled={isLoading}>
                            <div className="flex w-full justify-between">
                                <span>{t("userMenu.signOut")}</span>
                                {isLoading ? (
                                    <LoaderCircle className="h-[1.2rem] w-[1.2rem] animate-spin" />
                                ) : (
                                    <ExitIcon className="h-[1.2rem] w-[1.2rem]" />
                                )}
                            </div>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
                <AboutDialog open={showAboutDialog} onOpenChange={setShowAboutDialog} />
                <ReportBugDialog open={showBugReportDialog} onOpenChange={setShowBugReportDialog} />
            </SidebarMenuItem>
        </SidebarMenu>
    );
}
